import { useEffect, useRef, useState } from 'react';

interface UseInViewOptions {
    threshold?: number | number[];
    rootMargin?: string;
    triggerOnce?: boolean;
    skip?: boolean;
}

export const useInView = (options: UseInViewOptions = {}) => {
    const [inView, setInView] = useState(false);
    const [hasBeenInView, setHasBeenInView] = useState(false);
    const ref = useRef<HTMLElement>(null);

    const {
        threshold = 0.1,
        rootMargin = '0px',
        triggerOnce = false,
        skip = false
    } = options;

    useEffect(() => {
        const element = ref.current;
        if (!element || skip) return;

        // Check if IntersectionObserver is supported
        if (typeof window === 'undefined' || !window.IntersectionObserver) {
            // Fallback: assume element is always in view
            setInView(true);
            return;
        }

        const observer = new IntersectionObserver(
            ([entry]) => {
                const isIntersecting = entry.isIntersecting;

                setInView(isIntersecting);

                if (isIntersecting && !hasBeenInView) {
                    setHasBeenInView(true);

                    // If triggerOnce is true, stop observing after first intersection
                    if (triggerOnce) {
                        observer.unobserve(element);
                    }
                }
            },
            {
                threshold,
                rootMargin,
            }
        );

        observer.observe(element);

        return () => {
            observer.disconnect();
        };
    }, [threshold, rootMargin, triggerOnce, skip, hasBeenInView]);

    return { ref, inView, hasBeenInView };
};
